import React from 'react';
import { ChartDataPoint, tooltipDateLabel } from '../../axisUtils';
import Colors from '../../../constants/Colors';
import { alternateLineColor, selectedLineColor } from '../../AirQualityGraph';
import { comparisonLineColor } from '../../../components/Home/HomeGraphLineSelector';

interface TooltipProps {
  chartHeight: number,
  paddingTop: number,
  paddingBottom: number,
  domainLimit: number,
  title: string,
  alternateTitle?: string,
  chartData: ChartDataPoint[],
  alternateData?: ChartDataPoint[],
  comparisonData?: ChartDataPoint[],
  x?: number,
  y?: number,
  datum?,
  unit?: string,
  timeFormat?: boolean,
  stackAlternate?: boolean,
  alternateColor?: string,
  hideTimeOnTooltip?: boolean,
}

const Tooltip = ({
  title, alternateTitle, chartData, alternateData, comparisonData, chartHeight, stackAlternate,
  domainLimit, x, y, datum, unit, paddingTop, paddingBottom, alternateColor, hideTimeOnTooltip,
}: TooltipProps) => {
  const unitSuffix: string = ` ${unit?.toLowerCase() ?? ''}`;
  const tooltipCircle = (cx: number, value: number, stroke: string, limit: number) => !!value
    && <circle
      r={3}
      strokeWidth={6}
      {...{ stroke, cx }}
      cy={paddingTop + (chartHeight - 1 - paddingTop - paddingBottom) * (1 - value / limit)}
      fill={stroke}
      strokeOpacity={0.3}
    />;

  const chartDataPoint: ChartDataPoint = chartData?.[datum.index];
  const comparisonDataPoint: ChartDataPoint = comparisonData?.[datum.index];
  const alternateDataPoint: ChartDataPoint = alternateData?.[datum.index];
  const chartDataPointDate: string = chartDataPoint?.date;
  const comparisonDataPointDate: string = comparisonDataPoint?.date;
  const chartDataPointValue: number = chartDataPoint?.value;
  const comparisonDataPointValue: number = comparisonDataPoint?.value;
  const alternateDataPointValue: number = alternateDataPoint?.value;
  const date: Date = chartDataPointDate ? new Date(chartDataPointDate) : null;
  const dateValid: boolean = Number.isFinite(date?.getTime());
  const dateCompared: Date = comparisonDataPointDate ? new Date(comparisonDataPointDate) : null;
  const dateText: string = dateValid
    ? tooltipDateLabel(chartData, datum.index, false, !hideTimeOnTooltip)
    : chartDataPointDate ?? '-';
  const dateComparedText: string = dateCompared
    ? `vs. ${tooltipDateLabel(comparisonData, datum.index, false, !hideTimeOnTooltip)}` : null;
  let valueText: string = Number.isFinite(chartDataPointValue)
    ? (Math.floor(chartDataPointValue * 10) / 10)?.toString() : '-';
  valueText += unitSuffix;
  let alternateValueText: string = Number.isFinite(alternateDataPointValue)
    ? (Math.floor(alternateDataPointValue * 10) / 10)?.toString() : '-';
  alternateValueText += unitSuffix;
  let valueComparedText: string = Number.isFinite(comparisonDataPointValue)
    ? (Math.floor(comparisonDataPointValue * 10) / 10)?.toString() : '-';
  valueComparedText += unitSuffix;
  let containerHeight: number = comparisonData ? 76 : 56;
  if (alternateData && alternateTitle) containerHeight += 20;
  const containerWidth: number = (comparisonData ? 50 : 40)
    + Math.max(
      dateText.length,
      valueText.length + 7,
      alternateValueText.length + 7,
      dateComparedText?.length ?? 0,
      valueComparedText?.length ?? 0,
    ) * 9;
  const containerX: number = x > containerWidth + 20 ? x - containerWidth - 10 : x + 10;
  let containerY: number = Math.max(Math.min(y - containerHeight, 210 - containerHeight), 10);
  if (!Number.isFinite(containerY)) containerY = 10;

  const spot = (cy: number, fill: string) => <circle
    r={6}
    cx={containerX + 15}
    cy={containerY + cy}
    {...{ fill }}
  />;

  const label = (cy: number, labelTitle: string, text: string) => <text
    x={containerX + 30}
    y={containerY + cy}
    fontFamily={'Roboto_400Regular'}
    fontSize={12}
    strokeWidth={0}
    fill={Colors.light.hereworksBlack400}>
    {`${labelTitle}: `}
    {text}
    <text
      fontFamily={'Poppins_500Medium'}
      fill={Colors.light.hereworksBlack600}
    >
      {text}
    </text>
  </text>;

  const dateLabel = (cy: number, text: string) => <text
    x={containerX + 10}
    y={containerY + cy}
    fontFamily={'Poppins_500Medium'}
    fontSize={12}
    strokeWidth={0}
    fill={Colors.light.hereworksBlack400}>
    {text}
  </text>;

  return !!date && <svg>
    <defs>
      <filter id={'f3_desk_bookings'} x={-0.5} y={-1} width='200%' height='250%'>
        <feOffset result='offOut' in='SourceGraphic' dx='0' dy='0' />
        <feColorMatrix result='matrixOut'
          in='offOut'
          type='matrix'
          values='0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0' />
        <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='4' />
        <feBlend in='SourceGraphic' in2='blurOut' mode='normal' result='colored' />
      </filter>
    </defs>
    {!stackAlternate
      && tooltipCircle(x, chartDataPointValue, selectedLineColor, domainLimit)}
    <rect
      x={containerX}
      y={containerY}
      width={containerWidth}
      height={containerHeight}
      stroke={selectedLineColor}
      rx={5}
      fill='white'
      filter={'url(#f3_desk_bookings)'}
    />
    {datum.date && dateLabel(20, dateText)}
    {comparisonData && comparisonDataPointDate && dateLabel(40, dateComparedText)}
    {spot(comparisonData ? 60.5 : 40.5, selectedLineColor)}
    {label(comparisonData ? 65 : 45, title, valueText)}
    {alternateData && alternateTitle
      && spot(comparisonData ? 80.5 : 60.5, alternateColor ?? alternateLineColor)}
    {alternateData && alternateTitle
      && label(comparisonData ? 85 : 65, alternateTitle, alternateValueText)}
    {comparisonData && <>
      {spot(80.5, comparisonLineColor)}
      {label(85, `${title}(c)`, valueComparedText)}
    </>}
  </svg>;
};

export default Tooltip;
